/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react'
import {
  CanAccess,
  ITreeMenu,
  useLink,
  useMenu,
  useRouterContext,
  useRouterType,
} from '@refinedev/core'
import {
  ActionIcon,
  Box,
  Drawer,
  Navbar,
  NavLink,
  ScrollArea,
  MediaQuery,
  Tooltip,
  TooltipProps,
  Styles,
  Divider,
  Image,
  Badge,
  Group,
  Text,
} from '@mantine/core'
import {
  IconList,
  IconMenu2,
  IconChevronLeftPipe,
  IconChevronRightPipe,
} from '@tabler/icons-react'
import { RefineLayoutSiderProps } from '@refinedev/mantine'
import LOGO from '../../../../public/images/logo.png'
import { getStompClient } from 'src/utils/stompClient'
import { useContasStore } from 'src/stores/ConstasStore'

const defaultNavIcon = <IconList size={18} />

export const Menu: React.FC<RefineLayoutSiderProps> = ({ render, meta }) => {
  const [opened, setOpened] = useState(false)
  const contasReceber = useContasStore(state => state.receber)
  const contasPagar = useContasStore(state => state.pagar)
  const vendas = useContasStore(state => state.vendas)
  const comprasPendentes = useContasStore(state => state.compras)
  const orcamentosPendentes = useContasStore(state => state.orcamentos)
  const produtosEstoqueMinimo = useContasStore(state => state.produtos)
  const routerType = useRouterType()
  const NewLink = useLink()
  const { Link: LegacyLink } = useRouterContext()
  const Link = routerType === 'legacy' ? LegacyLink : NewLink
  const { defaultOpenKeys, menuItems, selectedKey } = useMenu({ meta })

  const [collapsed, setCollapsed] = useState(() => {
    const saved =
      typeof window !== 'undefined'
        ? localStorage.getItem('menu-collapsed')
        : false
    return saved === 'true'
  })

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      localStorage.setItem('menu-collapsed', String(!prev))
      return !prev
    })
  }

  const drawerWidth = () => (collapsed ? 80 : 240)
  const isCollapsed = collapsed

  const commonNavLinkStyles: Styles<any, any> = theme => ({
    root: {
      borderRadius: '0px 10px 10px 0px',
      width: '95%',
      display: 'flex',
      fontWeight: 500,
      transition: 'all 150ms linear',
      justifyContent: opened
        ? 'flex-start'
        : isCollapsed
        ? 'center'
        : 'flex-start',
      paddingLeft: opened ? undefined : isCollapsed ? 0 : undefined,

      '&:hover': {
        backgroundColor: theme.colorScheme === 'dark' ? '#ffffff1a' : '#E6F2FC',
      },

      '&[data-active]': {
        fontWeight: 700,

        borderLeft: `5px solid ${theme.colors.blue[6]}`,
        paddingLeft: opened ? 9 : isCollapsed ? 0 : 9,
        background:
          theme.colorScheme === 'dark'
            ? 'linear-gradient(145deg, #2a2a2a, #1f1f1f)'
            : 'linear-gradient(145deg, #ffffff, #e6f2fc)',

        boxShadow:
          theme.colorScheme === 'dark'
            ? `
            inset 1px 1px 2px rgba(255,255,255,0.05),
            inset -1px -1px 2px rgba(0,0,0,0.6),
            0 4px 10px rgba(0,0,0,0.4)
          `
            : `
            inset 1px 1px 2px rgba(255,255,255,0.9),
            inset -1px -1px 2px rgba(0,0,0,0.08),
            0 4px 12px rgba(0,0,0,0.12)
          `,

        transform: 'translateY(-1px)',
      },
    },

    icon: {
      marginRight: opened ? 12 : isCollapsed ? 0 : 12,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    body: {
      display: opened ? 'flex' : isCollapsed ? 'none' : 'flex',
    },
  })

  const commonTooltipProps: Partial<TooltipProps> = {
    disabled: !isCollapsed,
    position: 'right',
    withinPortal: true,
    withArrow: true,
    arrowSize: 8,
    arrowOffset: 12,
    offset: 4,
  }

  useEffect(() => {
    const client = getStompClient()

    client.onConnect = () => {
      client.subscribe('/topic/menu-counters', message => {
        const data = JSON.parse(message.body)
        useContasStore.setState(prev => ({
          ...prev,
          receber: data.contasreceber,
          pagar: data.contaspagar,
          vendas: data.vendas,
          compras: data.compras,
          orcamentos: data.orcamentos,
          produtos: data.produtos,
        }))
      })
    }

    client.activate()

    return () => {
      client.deactivate()
    }
  }, [])

  const renderTreeView = (tree: ITreeMenu[], selectedKey?: string) => {
    const countersMap: Record<string, number> = {
      contasreceber: contasReceber,
      contaspagar: contasPagar,
      vendas: vendas,
      compras: comprasPendentes,
      orcamentos: orcamentosPendentes,
      produtos: produtosEstoqueMinimo,
    }

    return tree.map(item => {
      const { icon, label, route, name, children } = item
      const isSelected = item.key === selectedKey
      const isParent = children.length > 0

      const counterValue = countersMap[name.toLowerCase()] ?? 0

      const customLabel = (
        <Group position="apart" w="100%" noWrap>
          <Text size="xs">{label}</Text>
          {counterValue > 0 && !isCollapsed && (
            <Badge size="sm" color="red" variant="light">
              <Text>{counterValue}</Text>
            </Badge>
          )}
        </Group>
      )

      const additionalLinkProps = isParent
        ? {}
        : { component: Link as any, to: route }

      return (
        <CanAccess
          key={item.key}
          resource={name.toLowerCase()}
          action="list"
          params={{ resource: item }}
        >
          <Tooltip
            label={counterValue > 0 ? `${label} (${counterValue})` : label}
            {...commonTooltipProps}
          >
            <NavLink
              key={item.key}
              label={customLabel}
              icon={icon ?? defaultNavIcon}
              active={isSelected}
              childrenOffset={12}
              defaultOpened={defaultOpenKeys.includes(item.key || '')}
              styles={commonNavLinkStyles}
              {...additionalLinkProps}
            >
              {isParent && renderTreeView(children, selectedKey)}
            </NavLink>
          </Tooltip>
        </CanAccess>
      )
    })
  }

  const items = renderTreeView(menuItems, selectedKey)

  const renderSider = () => {
    if (render) {
      return render({
        dashboard: undefined,
        items,
        collapsed: isCollapsed,
        logout: undefined,
      })
    }
    return <>{items}</>
  }

  return (
    <>
      {/* MOBILE */}
      <MediaQuery largerThan="md" styles={{ display: 'none' }}>
        <Box sx={{ position: 'fixed', top: 64, left: 0, zIndex: 1199 }}>
          <ActionIcon
            color="blue"
            size={40}
            sx={{ borderRadius: '0 6px 6px 0' }}
            onClick={() => setOpened(prev => !prev)}
          >
            <IconMenu2 />
          </ActionIcon>
        </Box>
      </MediaQuery>

      <MediaQuery largerThan="md" styles={{ display: 'none' }}>
        <Drawer
          p={0}
          opened={opened}
          onClose={() => setOpened(false)}
          m={0}
          size={240}
          zIndex={1200}
          withCloseButton={false}
        >
          <Navbar.Section>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Image width={190} height={190} src={LOGO.src} alt="Logo" />
            </Box>
          </Navbar.Section>
          <Navbar.Section p={0} component={ScrollArea} grow>
            {renderSider()}
          </Navbar.Section>
        </Drawer>
      </MediaQuery>

      {/* DESKTOP */}
      <MediaQuery smallerThan="md" styles={{ display: 'none' }}>
        <Box
          sx={{
            width: drawerWidth(),
            flexShrink: 0,
            transition: 'width 150ms linear',
          }}
        />
      </MediaQuery>

      <MediaQuery smallerThan="md" styles={{ display: 'none' }}>
        <Navbar
          width={{ base: drawerWidth() }}
          sx={{
            overflow: 'hidden',
            position: 'fixed',
            top: 0,
            height: '100vh',
            transition: 'width 150ms linear',
          }}
        >
          <Navbar.Section>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: isCollapsed ? 8 : 16,
              }}
            >
              <Image
                width={isCollapsed ? 40 : 190}
                height={isCollapsed ? 40 : 190}
                src={LOGO.src}
                alt="Logo"
              />
            </Box>
          </Navbar.Section>

          <Divider size="xs" />

          <Navbar.Section grow mt="sm" component={ScrollArea} mx="-xs" px="xs">
            {renderSider()}
          </Navbar.Section>

          <Navbar.Section>
            <Box
              p="xs"
              sx={{
                display: 'flex',
                justifyContent: isCollapsed ? 'center' : 'flex-end',
              }}
            >
              <ActionIcon variant="light" onClick={toggleCollapsed}>
                {collapsed ? <IconChevronRightPipe /> : <IconChevronLeftPipe />}
              </ActionIcon>
            </Box>
          </Navbar.Section>
        </Navbar>
      </MediaQuery>
    </>
  )
}
